import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

type FeatureFlags = {
  [featureFlag: string]: {
    name: string;
    description: string;
  };
};

export const featureFlags = {
  SESSION_TESTING: {
    name: "Session testing",
    description:
      "Show login and session failure testing controls in local and staging environments",
  },
  ORDER_PARTIAL_REFUNDS: {
    name: "Order partial refunds after confirmation",
    description:
      "Allows supplier to issue a partial refund, only after confirmation",
  },
  DISCOVERY_BESTSELLERS_ENABLED: {
    name: "Marketplace bestsellers",
    description: "Enables marketplace bestsellers",
  },
  CHECKOUT_CREDIT_TERMS_REQUEST: {
    name: "Show credit terms request on checkout & storefront & order content",
    description:
      "Allows users to select their credit terms on checkout, and for the supplier to accept or decline their request",
  },
  PALLET_OVERRIDE: {
    name: "Pallet delivery option override",
    description:
      "Turn on pallet/haulier delivery tier override for 50L+ containers for everyone",
  },
  VIDEO_COMPONENT_SPIKE: {
    name: "Embedded YouTube video component",
    description: "Enables example embedded video on market",
  },
  SUPPLIER_STOREFRONT_PREVIEW: {
    name: "Enable supplier storefront preview tool",
    description: "Enable the new supplier storefront preview tool",
  },
  SELLAR_IMPORTS_FINANCE_SECTION: {
    name: "Enable supplier sellar import finance section",
    description: "Enable supplier sellar import finance section",
  },
  TRADE_TERM_PLACEHOLDERS: {
    name: "Enable trade term placeholders",
    description: "Enable trade term placeholders",
  },
  SELLAR_CREDIT_PAYMENT_ALLOCATION_HISTORY: {
    name: "Display sellar credit payment allocation history tab",
    description: "Display sellar credit payment allocation history tab",
  },
  DISCOVERY_SORT_COMPARISON: {
    name: "Discovery ranking comparison",
    description:
      "Show Popular (old) and Recommended (new) market search ranking side by side so they can be compared in production",
  },
  PRODUCT_CONTAINER_ONBOARDING: {
    name: "Product container onboarding",
    description:
      "Guide suppliers without products through container and first-product setup",
  },
  DISCOVERY_SEARCH_REVAMP: {
    name: "New discovery search experience",
    description:
      "Force the new search experience in this browser for QA, including unauthenticated users",
  },
} as const satisfies FeatureFlags;

export type FeatureFlagKey = keyof typeof featureFlags;

type FeatureFlagsStoreState = {
  featureFlags: {
    [flag: string]: boolean;
  };
};

type FeatureFlagsStoreActions = {
  isActive: (flag: FeatureFlagKey) => boolean;
  toggleFlag: (flag: FeatureFlagKey) => void;
  reset: () => void;
};

const initialFeatureFlagStoreState: FeatureFlagsStoreState = {
  featureFlags: Object.keys(featureFlags).reduce(
    (featureFlagsState, current) => ({
      ...featureFlagsState,
      [current]: false,
    }),
    {},
  ),
};

export const useFeatureFlagStore = create<
  FeatureFlagsStoreState & FeatureFlagsStoreActions,
  [["zustand/persist", FeatureFlagsStoreState]]
>(
  persist(
    (set, get) => ({
      ...initialFeatureFlagStoreState,
      isActive: (flag) => get().featureFlags[flag] ?? false,
      toggleFlag: (flag) =>
        set(({ featureFlags: prev }) => {
          //if feature flag not defined in the current store, default to false
          const prevFlag = prev[flag] ?? false;
          return {
            featureFlags: {
              ...prev,
              [flag]: !prevFlag,
            },
          };
        }),
      reset: () => set(initialFeatureFlagStoreState),
    }),
    {
      name: "feature-flags",
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);
